import { useState } from "react";
import Cookies from "js-cookie";
import { usePlanState } from "../hooks/usePlanState";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Since accounts are auto-logged-in at signup (verify-later), a user can be inside
// the app with an unverified email. This banner nudges them to verify and lets them
// resend the link. Driven by plan-state.email_verified; hides itself once verified.
export default function VerifyEmailBanner() {
    const { planState, refetch } = usePlanState();
    const [sending, setSending] = useState(false);
    const [msg, setMsg] = useState(null); // { type: "ok" | "err", text }

    // Wait for plan-state to load, and only show while explicitly unverified.
    // (email_verified defaults to true server-side, so a load miss won't nag.)
    if (!planState || planState.email_verified !== false) return null;

    const resend = async () => {
        setSending(true);
        setMsg(null);
        try {
            const t = Cookies.get("token");
            const res = await fetch(`${API_URL}/api/user/resend-verification`, {
                method: "POST",
                headers: t ? { Authorization: `Bearer ${t}` } : {},
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json.error || json.message || "Couldn't send the email. Try again.");
            // If the account got verified in another tab, refresh state so the banner clears.
            if (json.alreadyVerified) refetch();
            setMsg({ type: "ok", text: json.message || "Verification email sent — check your inbox." });
        } catch (e) {
            setMsg({ type: "err", text: e.message });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="border-b border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/25 text-amber-900 dark:text-amber-100">
            <div className="max-w-6xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">
                    ✉️ Please verify your email to secure your account.
                    {msg && (
                        <span className={`ml-2 font-semibold ${msg.type === "ok" ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>
                            {msg.text}
                        </span>
                    )}
                </p>
                <button
                    onClick={resend}
                    disabled={sending}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60"
                >
                    {sending ? "Sending…" : "Resend email"}
                </button>
            </div>
        </div>
    );
}
